<template>
  <d-container
    fluid
    class="main-content-container px-4">
    <!-- Page Header -->
    <d-row
      no-gutters
      class="page-header py-4">
      <d-col
        col
        sm="6"
        class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">
          {{ $t('product.products') }}
        </span>
        <h3 class="page-title">
          {{ $t('product.tag.tagCategoriesList') }}
        </h3>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="success"
          @click.prevent="$router.push('/produkty/tagi/nowy')">
          <i class="material-icons">add</i>
          {{ $t('product.tag.addNewCategory') }}
        </d-button>
      </d-col>
    </d-row>

    <d-row>
      <d-button-group
        size="small"
        class="ml-auto mr-auto mt-2 mb-5">
        <d-button
          theme="primary"
          :outline="isTreeMode"
          @click.prevent="isTreeMode = false">
          {{ $t('product.tag.showAsList') }}
        </d-button>
        <d-button
          theme="primary"
          :outline="!isTreeMode"
          @click.prevent="isTreeMode = true">
          {{ $t('product.tag.showAsTree') }}
        </d-button>
      </d-button-group>
    </d-row>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0">{{ $t('product.tag.categoryName') }}</th>
            <th scope="col" class="border-0" style="width: 120px;">{{ $t('product.tag.numberOfTags') }}</th>
            <th scope="col" class="border-0" style="width: 100px;">{{ $t('ui.actions') }}</th>
          </tr>
        </thead>
        <draggable
          v-if="!loadError && !isTreeMode"
          v-model="categories"
          tag="tbody">
          <tr
            v-for="(category, index) of categories"
            :key="'category-' + index">
            <td class="align-middle">
              <i class="material-icons mr-2" style="font-size: 20px; position: relative; top: 4px; opacity: .5;">dehaze</i>

              <d-badge
                v-if="category.is_main"
                theme="success"
                class="mr-2">
                {{ $t('product.tag.isMain') }}
              </d-badge>
              {{ category.name }}
            </td>
            <td class="align-middle">
              {{ category.tags_count }}
            </td>
            <td class="align-middle" style="width: 150px;">
              <d-button
                theme="primary"
                @click.native="$router.push('/produkty/tagi/edycja/' + category.id)"
                class="mr-2"
                :title="$t('ui.edit')">
                <i class="material-icons">edit</i>
              </d-button>
              <d-button
                theme="danger"
                :disabled="removeInProgress"
                @click.native="removeCategory(category.id)"
                :title="$t('ui.delete')">
                <i class="material-icons">delete</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="categories.length === 0">
            <td
              colspan="8"
              class="text-center">
              {{ $t('product.tag.noTagCategoriesToDisplay') }}a&hellip;
            </td>
          </tr>
        </draggable>

        <tbody
          v-if="!loadError && isTreeMode">
          <template v-for="(category, index) of mainCategories">
            <tr :key="'main-category-' + index">
              <td class="align-middle">
                {{ category.name }}
              </td>
              <td class="align-middle">
                {{ category.tags_count }}
              </td>
              <td class="align-middle" style="width: 150px;">
                <d-button
                  theme="primary"
                  @click.native="$router.push('/produkty/tagi/edycja/' + category.id)"
                  class="mr-2"
                  :title="$t('ui.edit')">
                  <i class="material-icons">edit</i>
                </d-button>
                <d-button
                  theme="danger"
                  :disabled="removeInProgress"
                  @click.native="removeCategory(category.id)"
                  :title="$t('ui.delete')">
                  <i class="material-icons">delete</i>
                </d-button>
              </td>
            </tr>

            <tr
              v-for="(subcategory, subindex) of subCategories[category.id]"
              :key="'subcategory-' + index + '-' + subindex">
              <td class="align-middle">
                <span style="opacity: .5;" class="mr-1">&mdash;</span> {{ subcategory.name }}
              </td>
              <td class="align-middle">
                {{ subcategory.tags_count }}
              </td>
              <td class="align-middle" style="width: 150px;">
                <d-button
                  theme="primary"
                  @click.native="$router.push('/produkty/tagi/edycja/' + subcategory.id)"
                  class="mr-2"
                  :title="$t('ui.edit')">
                  <i class="material-icons">edit</i>
                </d-button>
                <d-button
                  theme="danger"
                  :disabled="removeInProgress"
                  @click.native="removeCategory(subcategory.id)"
                  :title="$t('ui.delete')">
                  <i class="material-icons">delete</i>
                </d-button>
              </td>
            </tr>
          </template>

          <tr v-if="uncategorized.length">
            <td colspan="3" style="background: #efefef;">
              <strong style="font-weight: bold;">
                {{ $t('product.tag.withoutMainCategory') }}
              </strong>
            </td>
          </tr>

          <tr
            v-for="(category, index) of uncategorized"
            :key="'without-main-category-' + index">
            <td class="align-middle">
              {{ category.name }}
            </td>
            <td class="align-middle">
              {{ category.tags_count }}
            </td>
            <td class="align-middle" style="width: 150px;">
              <d-button
                theme="primary"
                @click.native="$router.push('/produkty/tagi/edycja/' + category.id)"
                class="mr-2"
                :title="$t('ui.edit')">
                <i class="material-icons">edit</i>
              </d-button>
              <d-button
                theme="danger"
                :disabled="removeInProgress"
                @click.native="removeCategory(category.id)"
                :title="$t('ui.delete')">
                <i class="material-icons">delete</i>
              </d-button>
            </td>
          </tr>

          <tr v-if="categories.length === 0">
            <td
              colspan="8"
              class="text-center">
              {{ $t('product.tag.noTagCategoriesToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="!allDataLoaded"
        class="pb-3 pt-3 text-center overlay-loading">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>
  </d-container>
</template>

<script>
import vueDraggable from 'vuedraggable';
import debounce from 'lodash.debounce';
import FormUtils from '../utils/FormUtils.js';
import ListUtils from '../utils/ListUtils.js';

export default {
  name: 'product-tags',
  components: {
    'draggable': vueDraggable
  },
  computed: {
    allDataLoaded () {
      return this.loaded;
    },
    mainCategories () {
      return this.categories.filter(category => category.is_main);
    },
    subCategories () {
      let subcategories = {};

      this.categories.filter(category => !category.is_main && category.parents.length).forEach(category => {
        let parentIDs = category.parents.map(parent => parent.id);

        parentIDs.forEach(parentID => {
          if (!subcategories[parentID]) {
            subcategories[parentID] = [category];
          } else {
            subcategories[parentID].push(category);
          }
        });
      });

      return subcategories;
    },
    uncategorized () {
      return this.categories.filter(category => !category.is_main && !category.parents.length);
    }
  },
  watch: {
    categories () {
      if (!this.listIsReady) {
        return;
      }

      this.debouncedCategoriesOrdering();
    }
  },
  data() {
    return {
      isTreeMode: false,
      categories: [],
      loaded: false,
      loadError: false,
      listIsReady: false,
      removeInProgress: false,
      categoriesOrderSaveInProgress: false
    };
  },
  mounted () {
    this.loadFilteredData();
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      ListUtils.loadPaginatedItemsData(this, {
        endpoint: '/tags/categories/items',
        listField: 'categories',
        params: {
          with: ['parents']
        },
        successAction: () => {
          setTimeout(() => {
            this.listIsReady = true;
          }, 500);
        }
      });
    },
    removeCategory (categoryID) {
      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/tags/categories/delete/',
        id: categoryID,
        successTitle: this.$t('product.tag.removeCategorySuccessTitle'),
        successText: this.$t('product.tag.removeCategorySuccessText'),
        confirmTitle: this.$t('product.tag.removeCategoryConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('product.tag.removeCategoryErrorText'),
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    // eslint-disable-next-line
    debouncedCategoriesOrdering: debounce(function () {
      this.saveCategoriesOrder();
    }, 1000),
    saveCategoriesOrder () {
      this.categoriesOrderSaveInProgress = true;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/tags/categories/save-ordering',
        formData: {
          items: this.categories.map(category => category.id)
        },
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('product.tag.categoryOrderChangeErrorText'),
        successAction: () => {
          this.categoriesOrderSaveInProgress = false;
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
</style>

